import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/cn';

export type StatusType = 'lead' | 'campaign' | 'inbox' | 'dsar';

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent';

const STATUS_TONE: Record<StatusType, Record<string, Tone>> = {
  lead: {
    new: 'info',
    active: 'success',
    paused: 'warning',
    archived: 'neutral',
    suppressed: 'danger',
  },
  campaign: {
    draft: 'neutral',
    pending_approval: 'warning',
    scheduled: 'info',
    running: 'success',
    paused: 'warning',
    completed: 'neutral',
    failed: 'danger',
  },
  inbox: {
    pending: 'neutral',
    provisioning: 'info',
    healthy: 'success',
    warming: 'accent',
    burnout: 'warning',
    blocked: 'danger',
  },
  dsar: {
    pending: 'warning',
    in_progress: 'info',
    completed: 'success',
    failed: 'danger',
  },
};

const pillVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full font-medium border whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral:
          'bg-[var(--color-subtle)] text-[var(--color-fg-secondary)] border-[var(--color-border)]',
        success:
          'bg-[var(--color-success-light)] text-[var(--color-success-dark)] border-[var(--color-success)]',
        warning:
          'bg-[var(--color-warning-light)] text-[var(--color-warning-dark)] border-[var(--color-warning)]',
        danger:
          'bg-[var(--color-danger-light)] text-[var(--color-danger-dark)] border-[var(--color-danger)]',
        info:
          'bg-[var(--color-info-light)] text-[var(--color-info-dark)] border-[var(--color-info)]',
        accent:
          'bg-[var(--color-accent-subtle)] text-[var(--color-accent)] border-[var(--color-accent)]',
      },
      size: {
        sm: 'h-5 px-2 text-[10px]',
        md: 'h-6 px-2.5 text-xs',
      },
    },
    defaultVariants: { tone: 'neutral', size: 'sm' },
  },
);

export interface StatusPillProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>,
    Pick<VariantProps<typeof pillVariants>, 'size'> {
  type: StatusType;
  status: string;
  /** Override label, otherwise uses i18n key `${type}.status.${status}`. */
  label?: string;
}

export const StatusPill = React.forwardRef<HTMLSpanElement, StatusPillProps>(
  ({ className, type, status, size, label, ...props }, ref) => {
    const { t } = useTranslation();
    const tone = STATUS_TONE[type]?.[status] ?? 'neutral';
    const fallback = status.replace(/_/g, ' ');
    const display = label ?? t(`${type}.status.${status}`, { defaultValue: fallback });
    return (
      <span
        ref={ref}
        className={cn(pillVariants({ tone, size }), className)}
        data-status={status}
        data-status-type={type}
        {...props}
      >
        <span
          className="h-1.5 w-1.5 rounded-full bg-current opacity-80"
          aria-hidden
        />
        {display}
      </span>
    );
  },
);
StatusPill.displayName = 'StatusPill';
